import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface YouchuanEnhanceParams {
  /**
   * 任务ID
   */
  jobId: string;
  /**
   * 图像编号，必须是0、1、2或3
   * @values 0 | 1 | 2 | 3
   */
  imageNo: 0 | 1 | 2 | 3;
  /**
   * 回调URL，必须是有效的HTTP/HTTPS URL
   */
  callback?: string;
}

export interface YouchuanEnhanceResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_enhance
 *
 * Youchuan Enhance Image Generator
 *
 * Image enhancement implementation for Youchuan Open Platform (悠船开放平台).
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90/paths/~1v1~1tob~1enhance/post
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanEnhance } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await youchuanEnhance({
 *   jobId: 'your-job-id',
 *   imageNo: 0
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanEnhanceParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanEnhance(
  params: YouchuanEnhanceParams,
): Promise<YouchuanEnhanceResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_enhance',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanEnhanceResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Filter out usage field from response
          const { usage, ...cleanResource } = resource;
          resources.push({
            type: cleanResource.type || 'unknown',
            url: cleanResource.url || '',
            size: cleanResource.size,
            jobId: cleanResource.jobId,
            ...cleanResource,
          });
        }
      }
    }
  }

  return resources;
}
